// Trades & Finishing — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const TRADES_FINISHING: Record<string, TemplateSpecific> = {
  "trade-bricklaying": {
    whoFor:
      "Bricklaying gangs and subcontractors building brick and block walls on housing, commercial and civils sites need a bricklaying risk assessment before the principal contractor lets them load out a scaffold. This RAMS is written for the bricklayer and the labourer who serves them, covering the real risks of lifting blocks all day, mixing and handling mortar, and working off a rising scaffold. It is the document a PC or client will ask for at induction and refuse access without.",
    scope:
      "This RAMS covers the laying of facing brick and dense and aerated blockwork to walls, piers and cavity construction, including spot boarding, mixing or gauging mortar, and cutting units to size. It assumes the scaffold and any loading bays are erected, inspected and handed over by others under a separate work-at-height RAMS. It does not cover scaffold erection, structural steel padstones, or installation of pre-cast lintels by crane, which require their own method statements. Spot-board loading and the rate of lift are agreed with the scaffold contractor before work starts.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "Manual Handling Operations Regulations 1992 (as amended)",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — cement",
      "Work at Height Regulations 2005",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER) — bench/cut-off saws",
      "Control of Vibration at Work Regulations 2005",
      "HSE CIS26 — Cement and skin contact",
    ],
    sequence: [
      "Confirm the scaffold has a current handover tag and a working lift at the correct height; never lay above shoulder height — call up the next lift first.",
      "Load out the scaffold within the boards' rated capacity, spreading packs of blocks rather than stacking a single bay; agree the loading pattern with the scaffolder.",
      "Set up the mixer or gauge mortar at ground level, decanting cement carefully to limit dust and wearing nitrile gloves before any skin contact with wet mortar.",
      "Run the line and lay to the gauge, taking blocks one at a time and keeping loads close to the body; rotate cutting and laying duties across the gang to share the lifting.",
      "Cut bricks and blocks on a bench-mounted cut-off saw with water suppression and the guard in place, sited downwind and clear of others.",
      "Strike joints, sheet up and protect new work, then wash tools and clean cement off skin promptly before breaks and at the end of shift.",
    ],
    hazards: [
      {
        hazard:
          "Manual handling of heavy blocks — dense concrete blocks weigh 20 kg+ and are routinely lifted one-handed onto the line, causing back and shoulder injury",
        who: ["Bricklayers", "Labourers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Specify lighter or split blocks where the design allows and order seven-cut/half blocks to avoid on-site cutting of full units",
          "Lay at a comfortable height between knee and shoulder by managing the scaffold lift; never lay off the deck or above shoulder height",
          "Two-person handling for lintels and dense units over 20 kg and rotate gang members between laying, serving and cutting",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Cement dermatitis and chemical burns — wet mortar is highly alkaline and abrasive, kneeling and contact causes ulcerating burns and allergic dermatitis",
        who: ["Bricklayers", "Labourers"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Use cement with reduced hexavalent chromium and gauge mortar mechanically to limit direct handling",
          "Wear nitrile gloves and waterproof kneeling protection; never kneel directly in wet mortar",
          "Provide hot water and barrier/after-work cream; wash splashes off skin immediately and report any persistent reddening",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Respirable crystalline silica from cutting bricks and blocks on a bench/cut-off saw — chronic exposure causes silicosis",
        who: ["Bricklayers", "Labourers", "Adjacent trades"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Order pre-cut and special units to design out cutting wherever possible",
          "Cut only on a saw fitted with integral water suppression; never dry-cut masonry",
          "Site the saw downwind and away from other operatives and wear FFP3 respiratory protection when cutting",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Falls and dropped materials from the rising scaffold — laying above the guard rail or overloading boards",
        who: ["Bricklayers", "Labourers", "Persons below"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Work only off a tagged, handed-over scaffold and raise the lift before laying above shoulder height",
          "Load out within the platform's rated duty and keep edge protection and brick guards in place",
          "Sheet and net the working lift and exclude the area below loading bays",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Lime mortar exposure on heritage and conservation work — caustic to skin and eyes, hot-lime burns during slaking",
        who: ["Bricklayers", "Labourers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use pre-slaked lime putty in preference to slaking quicklime on site wherever the spec allows",
          "If slaking on site, keep operatives clear of the exotherm and wear goggles, gauntlets and a face shield",
          "Provide eyewash and decontamination; treat all lime splashes as chemical burns",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Nitrile/cement-resistant gloves",
      "Waterproof knee protection",
      "FFP3 mask when cutting",
      "Safety goggles",
      "Hi-vis and safety boots",
      "Hard hat",
    ],
    competence: [
      "CSCS card appropriate to the trade and grade",
      "NVQ/SVQ in Trowel Occupations or time-served bricklaying experience",
      "Abrasive wheels training for any operative using the cut-off saw (PUWER)",
      "COSHH/cement awareness briefing recorded at induction",
    ],
    permits: [
      "Scaffold handover/inspection tag in date",
      "Hot/abrasive cutting area agreed where dust suppression water is supplied",
    ],
    equipment: [
      "Brick/block cut-off saw with water suppression",
      "Cement/forced-action mixer",
      "Spot boards and mortar tubs",
      "Lines, pins, levels and gauge rods",
      "Brick trowels and jointers",
      "Wheelbarrows and material hoist",
    ],
    pcChecks: [
      "That cutting is on a water-suppressed saw with FFP3 specified — reviewers reject bricklaying RAMS that rely on dry-cutting with a dust mask",
      "That cement dermatitis controls (gloves, washing facilities, reduced-chromium cement) are named, not just listed as PPE",
      "That the scaffold loading and rate of lift are agreed with the scaffold contractor, with no laying above shoulder height",
    ],
    faqs: [
      {
        q: "Do I need a separate COSHH assessment for cement?",
        a: "Yes. Cement is a COSHH substance because wet mortar is highly alkaline and can cause severe dermatitis and chemical burns, and dry cement and cutting dust contain respirable silica. Your RAMS should reference a cement COSHH assessment that names skin protection, washing facilities and respiratory protection for cutting. HSE guidance CIS26 sets out the skin-contact controls. A generic line saying 'wear gloves' is not enough for a competent reviewer.",
      },
      {
        q: "How heavy can a block be before two people must lift it?",
        a: "There is no single legal weight limit under MHOR, but the HSE guideline figures and most site rules treat dense concrete blocks over 20 kg as a two-person or mechanically-assisted lift, and many sites cap one-handed laying blocks at lower weights. Because bricklayers lift one-handed onto the line, even a sub-20 kg block carries a higher risk than the guideline suggests. The practical control is to specify lighter aerated or split blocks, keep the laying height between knee and shoulder, and rotate the gang.",
      },
    ],
  },

  "trade-steel-fixing": {
    whoFor:
      "Steel fixers and groundworks gangs tying reinforcement for foundations, slabs, columns and retaining walls are the audience for this steel fixing method statement. The trade's defining risks — exposed rebar ends, working over mats of bars, and crane-delivered bundles — are not covered by a generic groundworks RAMS, which is why the principal contractor asks for a fixing-specific document. It is written for the fixer on the mat and the slinger taking deliveries.",
    scope:
      "This method statement covers the cutting, bending where done on site, placing and tying of reinforcement to drawings and bar bending schedules, including fixing starter bars, links and mesh. It covers offloading and distribution of rebar bundles by crane or telehandler under a separate lifting plan. It does not cover the concrete pour, formwork or the temporary works that support deep excavations, which are addressed in their own RAMS. Cap and edge protection to projecting bars is installed as fixing proceeds.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "Manual Handling Operations Regulations 1992 (as amended)",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "Lifting Operations and Lifting Equipment Regulations 1998 (LOLER)",
      "Work at Height Regulations 2005",
      "HSE GS150 — Health and safety in construction (rebar caps)",
    ],
    sequence: [
      "Receive the bar schedule and confirm cut/bent steel matches the drawings; check delivered bundles are tagged and free of damaged tie wire.",
      "Direct offloading of rebar bundles by the appointed slinger using tested chains/strops to a designated, level stacking area clear of the excavation edge.",
      "Distribute bars to the work face, keeping the route clear and never carrying long bars near overhead services or other operatives.",
      "Fix bottom mat, links and top mat to the schedule, tying with a power tool or hand tool and keeping fingers clear of the wire ends.",
      "Cap every projecting and starter bar immediately with proprietary protection — purpose-made caps or troughs, not mushroom caps alone where a fall onto bars is foreseeable.",
      "Provide and maintain boarded walkways and crawl boards across completed mats so operatives are not walking directly on tied steel.",
      "Tidy offcuts and tie-wire tails as work proceeds and keep the mat clear before the formwork and pour follow on.",
    ],
    hazards: [
      {
        hazard:
          "Impalement and puncture on projecting or starter bars — a fall or trip onto unprotected vertical rebar can be fatal",
        who: ["Steel fixers", "Following trades", "Site visitors"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Design out long projecting bars where possible and sequence so starters are fixed last",
          "Cap every projecting bar as it is fixed; where a person could fall onto bars, use impact-resistant troughs or caps rated for fall protection, not thin mushroom caps",
          "Maintain edge protection and clear access so no one works or walks at a height above unprotected bars",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard:
          "Cuts and lacerations from sheared bar ends and snapping tie wire",
        who: ["Steel fixers"],
        initial: { likelihood: 4, severity: 2 },
        controls: [
          "Use bars cut and bent off site to the schedule to reduce on-site shearing",
          "Use a powered tying tool to keep hands away from cut wire ends",
          "Wear cut-resistant gloves and trim tie-wire tails flush",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Eye injury from springing tie wire and bar ends whipping during tying and cutting",
        who: ["Steel fixers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Tie with the wire directed away from the face and body",
          "Use a powered tying tool that controls the wire ends",
          "Mandatory impact-rated eye protection at the work face",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Falls and slips from working over a mat of reinforcement — feet trap between bars and the surface is uneven",
        who: ["Steel fixers", "Following trades"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Lay boarded walkways and crawl boards across completed mats",
          "Sequence fixing to keep a clear access route and remove trip hazards as you go",
          "Wear supportive boots with good ankle protection and keep the work face tidy",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Struck-by during crane/telehandler offloading of heavy rebar bundles — bundles can swing, roll or shed bars",
        who: ["Steel fixers", "Slingers", "Plant operators"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Lift to an appointed-person lift plan using tested, certified lifting accessories",
          "Trained slinger/signaller controls the lift; exclusion zone under suspended loads",
          "Land bundles on level bearers in a designated stacking area and de-rig before approaching",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
    ],
    ppe: [
      "Cut-resistant (Level D/E) gloves",
      "Impact-rated eye protection",
      "Safety boots with midsole and ankle support",
      "Hi-vis",
      "Hard hat",
      "Knee protection for low fixing",
    ],
    competence: [
      "CSCS card and steel-fixing experience or NVQ in steel fixing",
      "Trained, appointed slinger/signaller for offloading",
      "Powered tying-tool familiarity (PUWER)",
      "Briefing on the bar bending schedule and rebar-cap standard",
    ],
    permits: [
      "Lift plan/permit for crane or telehandler offloading",
      "Excavation access permit where fixing is below ground level",
    ],
    equipment: [
      "Powered rebar tying tool and pliers/nips",
      "Bar-bending and cutting equipment",
      "Proprietary rebar caps and fall-protection troughs",
      "Crawl boards and boarded walkways",
      "Certified chains/strops for offloading",
      "Bar spacers and chairs",
    ],
    pcChecks: [
      "That every projecting and starter bar is capped, with fall-rated protection specified where a person could fall onto bars — plain mushroom caps alone are rejected",
      "That offloading references an appointed-person lift plan and a trained slinger, not just 'crane offload'",
      "That walkways/crawl boards are provided so operatives are not walking directly on tied mats",
    ],
    faqs: [
      {
        q: "Are plastic mushroom caps enough to protect projecting rebar?",
        a: "Only for protection against scratches and minor cuts. Where a person could fall onto vertical bars — for example over an excavation or off a working platform — thin mushroom caps will not prevent impalement and are not acceptable as fall protection. HSE guidance is to use impact-resistant troughs or caps designed and tested to absorb a fall, or to remove the fall risk entirely with edge protection. Your RAMS must distinguish between the two situations.",
      },
      {
        q: "Who is responsible for offloading the rebar delivery safely?",
        a: "The lift is a lifting operation under LOLER, so it must be planned by a competent appointed person and controlled on site by a trained slinger/signaller using certified, in-date lifting accessories. The steel-fixing RAMS should reference that lift plan rather than treat offloading as an afterthought. Bundles are heavy, can swing and can shed bars, so an exclusion zone under the load and a designated, level landing area are essential.",
      },
    ],
  },

  "trade-formwork": {
    whoFor:
      "Formwork carpenters and falsework gangs erecting, supporting and striking shutters for in-situ concrete need this formwork method statement before any pour. Because formwork is temporary works that must carry the full wet-concrete load, the principal contractor and the temporary works coordinator will not accept a generic carpentry RAMS — they need one tied to a temporary works design and a permit to strike. It is written for the formwork gang and the operative loading and releasing the shutters.",
    scope:
      "This method statement covers the erection, propping, alignment, inspection, loading and striking of wall, column, soffit and edge formwork and the associated falsework to a temporary works design. It covers application of release agent and fixing of stop ends and ties. It does not cover the reinforcement fixing or the concrete pour itself, nor the design of the temporary works, which is produced and checked by a temporary works engineer. No formwork is loaded or struck without the temporary works coordinator's authorisation.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "BS 5975 — Temporary works and falsework",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER) — nail guns",
      "Work at Height Regulations 2005",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — release agents",
      "Manual Handling Operations Regulations 1992 (as amended)",
    ],
    sequence: [
      "Confirm the temporary works design, the design check category and the loading/striking criteria with the temporary works coordinator before starting.",
      "Erect and prop the formwork and falsework strictly to the TW drawings, plumbing and aligning panels and fitting all ties and bracing.",
      "Apply release agent sparingly to faces with the COSHH controls in place, avoiding overspray and skin contact.",
      "Have the erected formwork and falsework inspected and signed off (permit to load) against the design before any reinforcement or concrete is placed.",
      "Monitor the shutters during the pour for movement, deflection or grout loss, with a means to stop the pour if anything moves.",
      "Strike only on the temporary works coordinator's written authority (permit to strike) once the concrete has reached the specified strength, following the designed striking sequence.",
      "Lower, clean and stack panels safely, withdrawing or flattening nails and protecting any projecting fixings.",
    ],
    hazards: [
      {
        hazard:
          "Falsework or formwork collapse during the pour — overloading, missing props or striking too early can cause sudden structural failure",
        who: ["Formwork gang", "Concreting gang", "Persons nearby"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Erect strictly to a checked temporary works design under BS 5975 with the correct prop layout and bracing",
          "Permit to load issued only after a competent inspection against the design; monitor during the pour with authority to stop",
          "Strike only on the TWC's written permit once the specified concrete strength is confirmed, following the designed sequence",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard:
          "Nail-gun injury — penetrating wounds and ricochet during panel assembly and stripping",
        who: ["Formwork carpenters"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use sequential-trip (not contact/bump) firing tools and never bypass the safety contact",
          "Train and authorise operatives under PUWER and keep the free hand clear of the firing line",
          "Disconnect air/battery before clearing jams or moving, and wear eye protection",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Release-agent exposure — skin irritation, dermatitis and inhalation of mineral-oil mist when spraying in enclosed forms",
        who: ["Formwork carpenters"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Select a low-hazard, water-based release agent where the spec allows and apply by roller/brush rather than spray",
          "Ventilate column and wall forms and wear chemical-resistant gloves; RPE where spraying in confined forms",
          "Provide washing facilities and follow the product COSHH assessment",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Falls from height erecting and striking tall wall and soffit shutters from working platforms",
        who: ["Formwork carpenters"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Use integral formwork access platforms with guard rails or a tied/inspected scaffold",
          "Maintain edge protection and never work off the formwork ties or bracing",
          "Sequence striking so operatives are never below or beside an unrestrained panel",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Struck-by during striking — panels, props or stop ends falling as fixings are released",
        who: ["Formwork carpenters", "Persons below"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Follow the designed striking sequence and support panels before releasing the last fixings",
          "Lower large panels with mechanical assistance or a tag-controlled lift; exclusion zone below",
          "De-nail and stack immediately so no loose panels are left propped",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Eye protection (nail-gun work)",
      "Chemical-resistant gloves for release agent",
      "Cut-resistant gloves for handling",
      "Safety boots",
      "Hi-vis",
      "Hard hat",
    ],
    competence: [
      "CSCS card and formwork/falsework carpentry experience",
      "Nail-gun (cartridge/pneumatic) training under PUWER",
      "Temporary works awareness; supervisor with formwork TW competence",
      "Briefing on the temporary works design and striking criteria",
    ],
    permits: [
      "Permit to load (TWC sign-off of erected formwork against design)",
      "Permit to strike (TWC written authority once strength achieved)",
    ],
    equipment: [
      "Proprietary panel/wall and soffit formwork systems",
      "Adjustable steel props and falsework",
      "Nail/finishing guns",
      "Release-agent rollers or sprayers",
      "Push-pull props and aligning struts",
      "Formwork access platforms",
    ],
    pcChecks: [
      "That the temporary works design and a named temporary works engineer/coordinator are referenced — reviewers reject formwork RAMS with no TW involvement",
      "That a permit-to-strike sequence and strength criteria are documented, not just 'strike when cured'",
      "That nail guns are specified as sequential-trip with PUWER-trained operators",
    ],
    faqs: [
      {
        q: "Who decides when formwork can be struck?",
        a: "Striking is authorised by the temporary works coordinator, in writing, against criteria set by the temporary works engineer — usually a specified concrete strength confirmed by cube results or maturity monitoring, not just an elapsed time. Striking early or out of sequence can cause the partially-cured element or the falsework to fail. The formwork RAMS must reference the TW design and the permit-to-strike procedure rather than leave the decision to the gang on site. This is a core BS 5975 control.",
      },
      {
        q: "Is formwork classed as temporary works?",
        a: "Yes. Formwork and the falsework supporting it carry the full load of wet concrete plus construction loads, so under BS 5975 they are temporary works requiring a design, an appropriate design check and a temporary works coordinator. A formwork RAMS that does not reference a temporary works design will be rejected by any competent principal contractor. The RAMS describes how the gang erects, loads and strikes to that design; the design itself is a separate, checked document.",
      },
    ],
  },

  "trade-concreting": {
    whoFor:
      "Concreting gangs placing, compacting and finishing in-situ concrete — by skip, pump or direct discharge — should issue this concreting method statement to the principal contractor before the pour. The trade's worst injuries come from kneeling in wet concrete and from pump-line failures, hazards a generic groundworks RAMS glosses over. It is written for the gang on the pour, the pump operator and the operative running the poker.",
    scope:
      "This RAMS covers the placing, vibrating, levelling, floating and curing of structural and slab concrete delivered by ready-mix truck, concrete pump or crane skip. It covers setting up the pump line, washout and disposal of returned concrete and wash water. It does not cover the formwork, falsework or reinforcement that must already be inspected and signed off, nor the pump's own LOLER thorough examination, which is the pump contractor's responsibility. The pour does not start until the permit to load on the formwork is in place.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — wet cement",
      "Control of Vibration at Work Regulations 2005",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER) — pump and pokers",
      "Manual Handling Operations Regulations 1992 (as amended)",
      "HSE CIS26 — Cement and skin contact",
    ],
    sequence: [
      "Confirm the formwork/falsework is inspected and the permit to load is in place, and brief the gang on the pour sequence and rate.",
      "Set up and prime the pump and pipeline, securing pipe couplings and clamps and restraining the placing end against whip.",
      "Place concrete in even layers to the agreed rate, keeping the gang clear of the line and never exceeding the pour rate on which the formwork was designed.",
      "Compact with poker vibrators, rotating operatives to manage hand-arm vibration exposure and avoiding over-vibration that could surcharge the forms.",
      "Level, float and finish the surface, using kneeling protection and barrier cream as operatives work in and over wet concrete.",
      "On completion, depressurise and clean the line safely before breaking couplings, and direct washout to a designated washout area, not to drains or watercourses.",
    ],
    hazards: [
      {
        hazard:
          "Cement burns from kneeling and prolonged skin contact — wet concrete causes deep alkaline ulcers and is the most serious injury type in the trade",
        who: ["Concrete finishers", "Concreting gang"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Reduce kneeling time by using long-handled floats and screeds where the finish allows",
          "Wear waterproof, cement-resistant gloves and proper waterproof knee protection — never kneel directly in concrete",
          "Provide hot water, barrier cream and immediate decontamination; wash splashes off at once and check skin for burns each shift",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Pump-line blockage and blow-out — a blocked line can clear violently, whipping the hose and ejecting concrete under pressure",
        who: ["Concreting gang", "Pump operator", "Persons nearby"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Prime and operate the pump per the manufacturer's method; keep the line as straight as practicable and avoid sharp reductions",
          "Secure all couplings and clamps and restrain the placing hose; keep personnel clear of the line and never look into it",
          "If it blocks, stop, reverse and depressurise before breaking any coupling; operatives stand clear during clearing",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Hand-arm vibration from poker/immersion vibrators used continuously during the pour",
        who: ["Concreting gang"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Select low-vibration pokers and plan the pour to keep individual trigger time within the EAV/ELV",
          "Rotate operatives off the poker and record exposure against the Control of Vibration at Work Regulations",
          "Keep equipment maintained and report tingling or numbness through health surveillance",
        ],
        residual: { likelihood: 3, severity: 3 },
      },
      {
        hazard:
          "Washout and wash-water disposal — highly alkaline runoff harms drains, watercourses and skin",
        who: ["Concreting gang", "Environment"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Designate a bunded/lined washout area away from drains and watercourses and agree it with the PC",
          "Capture and allow wash water to settle/neutralise before controlled disposal; never wash into surface drains",
          "Wear gloves and eye protection when handling washout",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Manual handling and slips during placing — dragging concrete, handling the heavy placing hose on a slippery surface",
        who: ["Concreting gang"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Place by pump or skip to reduce barrowing and dragging, and route the line to minimise handling",
          "Two operatives to manage the placing hose; keep access boards and walkways clear of spilt concrete",
          "Maintain good footing with appropriate boots and tidy spillages immediately",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "Cement-resistant gloves and arm protection",
      "Waterproof knee pads/protection",
      "Wellingtons/waterproof boots",
      "Eye protection",
      "Hi-vis and hard hat",
      "Hearing protection near the pump",
    ],
    competence: [
      "CSCS card and concreting/finishing experience",
      "Pump operator competence and pump-priming briefing",
      "Vibration/HAVS awareness and health surveillance enrolment",
      "Cement/COSHH skin-contact briefing recorded",
    ],
    permits: [
      "Permit to load on the formwork before the pour",
      "Pour authorisation/concrete delivery and rate agreed with the TWC",
    ],
    equipment: [
      "Concrete pump and pipeline or crane skip",
      "Poker/immersion vibrators",
      "Power float and long-handled bull floats/screeds",
      "Levelling screeds and trowels",
      "Bunded washout area/skip",
      "Curing compound or membranes",
    ],
    pcChecks: [
      "That cement-burn controls for finishers — knee protection, gloves, washing, no direct kneeling — are explicit, given this is the trade's most serious injury",
      "That pump-line setup names blockage/blow-out controls and a clearing procedure, not just 'operate pump safely'",
      "That HAVS exposure from pokers is managed with rotation and trigger-time limits under the Vibration Regulations",
    ],
    faqs: [
      {
        q: "Why are cement burns such a problem for concrete finishers?",
        a: "Wet concrete is strongly alkaline and abrasive, and finishers kneel and lean in it for long periods, so it can soak through clothing and cause deep, slow-healing chemical burns — often without immediate pain. These are routinely the most serious injuries in the concreting trade. The controls are to minimise kneeling with long-handled tools, wear genuinely waterproof knee protection and cement-resistant gloves, and wash any contact off immediately with hot water. The RAMS should treat this as a primary hazard, not a footnote under COSHH.",
      },
      {
        q: "What should we do if the pump line blocks?",
        a: "Stop pumping immediately and do not break any coupling under pressure — a blocked line stores energy and can blow out violently when opened. The line should be reversed and fully depressurised following the pump manufacturer's procedure before any operative approaches a coupling, and everyone stands clear during clearing. Keeping the line straight, primed correctly and properly clamped reduces the chance of a blockage in the first place. The method statement should set out this stop-reverse-depressurise sequence.",
      },
    ],
  },

  "trade-joinery": {
    whoFor:
      "First- and second-fix carpenters and bench joiners cutting, machining and fixing timber on site are the audience for this carpentry risk assessment. The signature risks — circular-saw and router contact, carcinogenic hardwood dust and nail-gun injuries — are specific to the trade and not addressed by a generic site RAMS, so principal contractors expect a joinery-specific document at induction. It suits the carpenter using powered hand tools and the operative on a bench or cut-off saw.",
    scope:
      "This RAMS covers first-fix and second-fix carpentry and joinery: cutting and fixing studwork, floor and roof timbers, door linings, skirtings, architraves, stairs, kitchens and bench-machined items. It covers the use of portable circular saws, routers, planers and nail guns on site. It does not cover structural timber-frame erection by crane or the cutting of treated/preserved timber requiring additional COSHH controls, which are covered separately. Cutting is positioned and extracted to control wood dust at source.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER) — saw and tool guarding",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — wood dust",
      "EH40/2005 Workplace Exposure Limits — hardwood dust 3 mg/m3, softwood 5 mg/m3",
      "Control of Vibration at Work Regulations 2005",
      "HSE WIS guidance — wood dust and woodworking machinery",
    ],
    sequence: [
      "Plan cutting at a fixed, well-lit station with on-tool or local extraction, away from other trades.",
      "Inspect saws, routers and guards before use; confirm guards, riving knives and push sticks are fitted and the tool is PAT-tested.",
      "Cut and machine timber using extraction at source, push sticks and the correct guard, never freehand against the fence.",
      "Fix first-fix carcassing — studs, joists, noggins — keeping the free hand clear of the nail-gun firing line and using sequential-trip firing.",
      "Carry out second-fix joinery, scribing and fitting, with offcuts and dust cleared regularly rather than left to build up.",
      "Use solvent-based adhesives only with ventilation and gloves, keeping containers closed and away from ignition.",
      "Clean down by vacuum or damp method — never dry-sweep or blow dust off with airlines.",
    ],
    hazards: [
      {
        hazard:
          "Contact with circular-saw blade or router cutter — severe lacerations and amputation from kickback or freehand cutting",
        who: ["Carpenters", "Joiners"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Use saws with the riving knife and crown/blade guard fitted and never remove guards (PUWER)",
          "Use a push stick for the last 300 mm and never cut freehand against the fence; support long lengths",
          "Trained operatives only; isolate before changing blades/cutters and keep the free hand clear",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Hardwood dust exposure — hardwood dust is a Group 1 carcinogen (nasal cancer) with a WEL of 3 mg/m3 (softwood 5 mg/m3)",
        who: ["Carpenters", "Joiners", "Adjacent trades"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Cut and sand with on-tool LEV/dust extraction at source and a fixed cutting station to contain dust",
          "Vacuum or damp-clean — never dry-sweep or use compressed air to clear dust",
          "FFP3 RPE for dusty operations and health surveillance/face-fit for regular machining",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Nail-gun injury — penetrating wounds and ricochet during first-fix carcassing",
        who: ["Carpenters"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use sequential-trip firing tools, never contact/bump mode for repetitive work",
          "Keep the free hand and other operatives clear of the firing line; never disable the safety nose",
          "Disconnect before clearing jams or carrying, and wear eye protection",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Solvent-adhesive vapour and flammability — contact adhesives and PU foams give off vapour and ignite easily",
        who: ["Carpenters", "Joiners"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Substitute low-solvent/water-based products where the spec allows and use the COSHH assessment",
          "Ventilate the area, keep lids on and store away from ignition sources and hot-work areas",
          "Wear nitrile gloves and avoid skin contact; no smoking/naked flame nearby",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Hand-arm vibration from prolonged use of routers, sanders and impact drivers",
        who: ["Carpenters", "Joiners"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Select low-vibration tools and limit continuous trigger time within the EAV",
          "Rotate tasks and keep tools sharp and maintained to reduce effort",
          "Record exposure and include operatives in HAVS health surveillance",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "FFP3 dust mask (face-fit tested)",
      "Eye protection for cutting/nailing",
      "Hearing protection at machines",
      "Cut-resistant gloves for handling (not at the blade)",
      "Hi-vis and safety boots",
      "Hard hat where required",
    ],
    competence: [
      "CSCS card and NVQ/SVQ in Site Carpentry or Bench Joinery",
      "Woodworking-machinery/abrasive-wheels training for powered saws (PUWER)",
      "Nail-gun training and authorisation",
      "COSHH wood-dust awareness and health surveillance",
    ],
    permits: [
      "PAT/inspection record for portable powered tools",
      "Hot-work permit only where solvent products are used near ignition sources",
    ],
    equipment: [
      "Portable circular and cut-off saws with guards",
      "Routers and planers with extraction",
      "Nail/finishing guns",
      "On-tool LEV/dust extractor and M-class vacuum",
      "Push sticks and bench/cutting station",
      "Hand tools, clamps and levels",
    ],
    pcChecks: [
      "That wood dust is controlled at source with LEV and FFP3, and the hardwood 3 mg/m3 WEL is recognised — reviewers reject RAMS that treat dust as a nuisance",
      "That saws are specified with guards/riving knives and push sticks, with no freehand cutting",
      "That nail guns are sequential-trip with trained operators, and dry-sweeping/airlines for clean-down are prohibited",
    ],
    faqs: [
      {
        q: "Is wood dust really a COSHH issue?",
        a: "Yes, and a significant one. Hardwood dust is a recognised human carcinogen linked to nasal cancer, with a workplace exposure limit of 3 mg/m3 (softwood is 5 mg/m3) under EH40. That means cutting and sanding need extraction at source, the workshop or cutting station kept clean by vacuum rather than sweeping, FFP3 respiratory protection and health surveillance for regular exposure. A carpentry RAMS that does not name wood dust as a COSHH hazard will be sent back.",
      },
      {
        q: "Can I dry-sweep sawdust at the end of the day?",
        a: "No. Sweeping or blowing sawdust with an airline throws fine respirable dust back into the air, defeating the extraction you used while cutting. Clean down with an M-class vacuum or a damp method instead. This is a standard COSHH control for wood dust and is one of the things a principal contractor's reviewer will look for in the method statement.",
      },
    ],
  },

  "trade-plastering": {
    whoFor:
      "Plasterers floating, skimming and rendering internal walls and ceilings should hand the principal contractor this plastering risk assessment before starting. The trade's real burdens — repetitive shoulder strain from skimming overhead, dust clouds from mixing, and falls from hop-ups — are specific to plastering and rarely captured by a generic finishing RAMS. It is written for the plasterer on the trowel and the labourer mixing and serving.",
    scope:
      "This RAMS covers internal plastering: floating, skimming and finishing to walls and ceilings, fixing and beading angles, and patch and repair work, including mixing gypsum and bonding plasters. It covers low-level access on hop-ups and proprietary podium steps. It does not cover external rendering at height off scaffold, dry-lining/plasterboard fixing, or any hot work beyond occasional bead trimming with a grinder, which are addressed separately. Mixing is positioned to control airborne dust at the point of mixing.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "Manual Handling Operations Regulations 1992 (as amended)",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — plaster/silica dust",
      "Work at Height Regulations 2005 — hop-ups and podiums",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "HSE WAHR guidance on low-level access equipment",
    ],
    sequence: [
      "Set up a mixing station with the mixer at a sensible height, decanting bagged plaster carefully to limit dust clouds.",
      "Mix gypsum/bonding plaster to the right consistency, keeping the area ventilated and wearing a dust mask while bag emptying and mixing.",
      "Fix beads and stops, trimming with snips where possible and only occasionally cutting with a grinder under hot-work/dust controls.",
      "Float and rule the backing coat, working at a height that keeps the trowel below shoulder where possible and rotating overhead work.",
      "Skim and finish, using a proprietary podium step or hop-up for ceilings rather than over-reaching from the floor.",
      "Clean tools and buckets in a designated area, settling out plaster waste rather than washing into drains, and clear dust by vacuum/damp method.",
    ],
    hazards: [
      {
        hazard:
          "Repetitive strain and shoulder/upper-limb injury from skimming overhead and continuous trowelling",
        who: ["Plasterers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Plan the work to keep the trowel below shoulder height where possible and use podiums to reduce reach to ceilings",
          "Rotate overhead and wall work across the day and take micro-breaks to recover",
          "Keep trowels and tools in good condition to reduce effort, and report aches early",
        ],
        residual: { likelihood: 3, severity: 2 },
      },
      {
        hazard:
          "Plaster and silica dust inhalation when emptying bags and mixing — repeated exposure to gypsum and any silica content",
        who: ["Plasterers", "Labourers"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Open bags low and slowly into the mixing vessel to limit the dust cloud, with mixing in a ventilated area",
          "Use FFP3 RPE for bag-emptying and mixing and damp-clean/vacuum rather than dry-sweep",
          "Choose low-dust pre-mixed products where practicable",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Falls from hop-ups, trestles and podium steps when reaching ceilings and high walls",
        who: ["Plasterers", "Labourers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use proprietary podium steps with guard rails for sustained ceiling work rather than hop-ups or trestles",
          "Keep hop-ups on firm level ground, one operative at a time, and never over-reach — move the platform",
          "Inspect access equipment before use and keep platforms clear of tools underfoot",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Eye and skin contact with wet plaster and slips from spillage on the floor",
        who: ["Plasterers", "Labourers"],
        initial: { likelihood: 3, severity: 2 },
        controls: [
          "Wear eye protection for overhead skimming and gloves where prolonged contact occurs",
          "Catch drips and clear spillages immediately to keep footing on access platforms",
          "Provide washing facilities and wash plaster off skin and eyes promptly",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Hot work and dust when cutting metal beads with an angle grinder",
        who: ["Plasterers"],
        initial: { likelihood: 2, severity: 3 },
        controls: [
          "Cut beads with tin snips wherever possible and only grind under a hot-work permit if heat/sparks are present",
          "Use a guarded grinder with eye and face protection and FFP3 for the dust",
          "Keep combustibles clear and have a fire extinguisher to hand during any grinding",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
    ],
    ppe: [
      "FFP3 mask for mixing/bag emptying",
      "Eye protection for overhead work",
      "Nitrile gloves for prolonged contact",
      "Knee protection for low patching",
      "Hi-vis and safety boots",
      "Hard hat where required",
    ],
    competence: [
      "CSCS card and NVQ/SVQ in Plastering or equivalent experience",
      "Manual-handling and COSHH dust awareness briefing",
      "Podium/hop-up safe-use briefing under WAHR",
      "Abrasive-wheels training if grinding beads",
    ],
    permits: [
      "Hot-work permit only where beads are cut with a grinder near combustibles",
      "Access-equipment inspection record for podiums/hop-ups",
    ],
    equipment: [
      "Forced-action plaster mixer and whisk",
      "Floats, trowels and feather edges/rules",
      "Proprietary podium steps and hop-ups",
      "Buckets, spot boards and stilts where permitted",
      "M-class vacuum for clean-down",
      "Bead snips and (occasional) angle grinder",
    ],
    pcChecks: [
      "That low-level access is by inspected podium steps for ceiling work, not improvised hop-ups or stilts without controls",
      "That mixing/bag-emptying dust is controlled with FFP3 and ventilation, with no dry-sweeping",
      "That the upper-limb/MSD risk from overhead skimming is recognised with rotation and work-height planning",
    ],
    faqs: [
      {
        q: "Are plasterers allowed to use stilts?",
        a: "Stilts are not banned outright, but many principal contractors prohibit them or require a specific risk assessment because they raise the centre of gravity, prevent quick recovery from a stumble and increase the fall consequence. A proprietary podium step with guard rails is the safer and more readily accepted means of reaching ceilings. If stilts are used, the RAMS must justify it, set out a clear floor area, training and supervision, and the site rules still take precedence. Many sites will simply refuse them.",
      },
      {
        q: "Do I need a dust mask just for mixing plaster?",
        a: "Yes — emptying bags and mixing throws up a cloud of fine plaster dust, and some plasters and the substrates being prepared can contain silica, so FFP3 respiratory protection is appropriate for bag-emptying and mixing. Open bags low and slowly to cut the dust at source, mix in a ventilated spot, and clean down by vacuum or damp method rather than sweeping. These are standard COSHH controls a reviewer will expect to see in a plastering RAMS.",
      },
    ],
  },

  "trade-painting": {
    whoFor:
      "Painters and decorators preparing and coating internal and external surfaces should give the principal contractor this painting and decorating risk assessment before they start. Two trade-specific issues drive it: solvent and isocyanate exposure when spraying, and the chance of disturbing lead paint in pre-1970s buildings. It is written for the decorator brushing, rolling and spraying, and the operative sanding down old coatings.",
    scope:
      "This RAMS covers surface preparation, sanding, filling, priming and applying paints, varnishes and coatings by brush, roller and spray to internal and external surfaces, including step and stairwell access. It covers storage and use of solvent-based products. It does not cover removal of confirmed lead paint by specialist methods, work at height off scaffold/MEWP, or industrial protective coatings requiring breathing-air systems, which are covered by separate method statements. Pre-1970s surfaces are presumed to contain lead until tested.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — solvents, isocyanates",
      "Control of Lead at Work Regulations 2002",
      "Dangerous Substances and Explosive Atmospheres Regulations 2002 (DSEAR) — flammable storage",
      "Work at Height Regulations 2005 — steps and stairwell access",
      "EH40/2005 Workplace Exposure Limits",
    ],
    sequence: [
      "Assess the surface and building age; presume pre-1970s paint contains lead until a test proves otherwise, and plan controls accordingly.",
      "Prepare surfaces using wet/dustless sanding with extraction rather than dry abrasion, especially on old coatings.",
      "Set up safe access — Class 1 stepladders, podiums or stair-specific platforms — never an unsecured ladder leaned across a stairwell.",
      "Apply primers and undercoats by brush/roller in ventilated rooms, keeping solvent containers closed when not in use.",
      "Where spraying, isolate the area, ventilate or extract and wear appropriate RPE for solvents/isocyanates, controlling overspray.",
      "Store flammable products in a fire-rated cabinet/compound away from ignition, returning part-used tins to store at the end of each shift.",
    ],
    hazards: [
      {
        hazard:
          "Solvent and isocyanate exposure when spraying — vapour and mist cause respiratory sensitisation (occupational asthma) and headaches",
        who: ["Decorators", "Sprayers", "Adjacent occupants"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Brush/roller in preference to spraying and choose low-solvent/water-based products where the spec allows",
          "If spraying, isolate and ventilate the area and use RPE suited to the product — air-fed/half-mask with the correct filters for isocyanates",
          "Health surveillance for regular sprayers and exclude others from the spray zone",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Lead exposure from sanding old paint in pre-1970s buildings — inhalation/ingestion of lead dust",
        who: ["Decorators", "Others on site", "Occupants"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Presume old paint is leaded and test where in doubt; avoid dry sanding and heat-stripping of leaded coatings",
          "Use wet/dustless sanding with extraction, contain the area and clean by HEPA vacuum",
          "RPE, no eating/smoking in the area, hand-washing before breaks and blood-lead monitoring if significant work is foreseeable",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Falls from steps and platforms around stairwells, landings and external elevations",
        who: ["Decorators"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use the right access — podiums, stair platforms or a properly footed Class 1 stepladder — for the task, not the nearest ladder",
          "Never bridge a stairwell with a single ladder/plank; use a proprietary stairwell platform",
          "Maintain three points of contact, don't over-reach, and keep access ways clear of tins and trailing leads",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Fire and explosion from flammable solvents, thinners and rags — vapour build-up and spontaneous combustion of soaked rags",
        who: ["Decorators", "All on site"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Store solvents and thinners in a fire-rated cabinet/compound away from ignition (DSEAR)",
          "Keep only the working quantity at the workface, lids on, and ventilate to prevent vapour build-up",
          "Dispose of solvent-soaked rags in a closed metal container; no smoking or hot work near coating operations",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard:
          "Skin and eye contact and dermatitis from solvents, fillers and prolonged paint contact",
        who: ["Decorators"],
        initial: { likelihood: 3, severity: 2 },
        controls: [
          "Wear nitrile gloves and eye protection when handling solvents and during overhead work",
          "Use barrier cream and washing facilities; avoid solvent washing of skin",
          "Follow each product's COSHH assessment for splash and contact controls",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "RPE suited to product (half-mask/air-fed for spraying)",
      "FFP3 for sanding old coatings",
      "Nitrile gloves",
      "Eye protection",
      "Disposable coveralls for spray/lead work",
      "Hi-vis and safety footwear",
    ],
    competence: [
      "CSCS card and NVQ/SVQ in Painting and Decorating",
      "Spray-application training and face-fit testing for RPE",
      "Lead-awareness briefing under the Control of Lead at Work Regulations",
      "DSEAR/flammable-storage awareness",
    ],
    permits: [
      "Spraying permit/area isolation where solvent or isocyanate spraying is carried out",
      "Lead-work controls/notification where significant lead disturbance is identified",
    ],
    equipment: [
      "Brushes, rollers and trays",
      "Airless/HVLP spray equipment with overspray control",
      "Orbital sanders with dust extraction",
      "Class 1 stepladders and stairwell platforms/podiums",
      "Fire-rated solvent storage cabinet",
      "HEPA vacuum for old-coating clean-down",
    ],
    pcChecks: [
      "That spraying RPE is matched to the product — air-fed/correct-filter for isocyanates — not a generic dust mask",
      "That pre-1970s coatings are presumed leaded with wet/dustless sanding and HEPA clean-up, not dry sanding",
      "That flammable storage is fire-rated and away from ignition, with a soaked-rag disposal control",
    ],
    faqs: [
      {
        q: "How do I know if old paint contains lead?",
        a: "Treat paint in any building built or last decorated before about 1970 as containing lead unless a test proves otherwise. Lead-paint test kits or laboratory sampling can confirm it, but the safe default is to assume it is present and avoid dry sanding or heat-stripping, which generate lead dust and fume. Use wet or dustless sanding with extraction, contain the area and clean up with a HEPA vacuum, and follow the Control of Lead at Work Regulations. The RAMS should state this presumption clearly.",
      },
      {
        q: "What mask do I need for spray painting?",
        a: "It depends on the product. For solvent-based paints a half-mask with the correct organic-vapour filters may be adequate in a ventilated space, but for two-pack and polyurethane coatings containing isocyanates an air-fed mask is normally required because isocyanates cause occupational asthma at very low levels. Whatever RPE is chosen must be face-fit tested and matched to the COSHH assessment, and other people kept out of the spray zone. A standard FFP dust mask is not suitable for spraying solvents.",
      },
    ],
  },

  "trade-flooring": {
    whoFor:
      "Floor layers fitting vinyl, carpet, resin, LVT and resilient flooring should issue the principal contractor this flooring risk assessment before starting. The trade is defined by all-day kneeling, flammable contact adhesives in unventilated rooms, and handling heavy rolls — none of which a generic finishing RAMS covers properly. It is written for the floor layer preparing subfloors and bonding finishes, and the operative handling and cutting materials.",
    scope:
      "This RAMS covers subfloor preparation (grinding, priming, latex smoothing), and the laying of sheet vinyl, carpet, LVT, resilient and resin floor finishes using adhesives or loose-lay/click systems. It covers handling and cutting of rolls and tiles. It does not cover removal of asbestos floor tiles or bitumen adhesive, screed pouring, or epoxy resin systems requiring forced ventilation and confined-space controls, which are covered separately. Adhesive selection and room ventilation are confirmed before bonding starts.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "Manual Handling Operations Regulations 1992 (as amended)",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — adhesives, latex, silica",
      "Dangerous Substances and Explosive Atmospheres Regulations 2002 (DSEAR) — flammable adhesives",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER) — grinders, knives",
      "EH40/2005 Workplace Exposure Limits — RCS",
    ],
    sequence: [
      "Prepare the subfloor — grind, scarify and clean — using on-tool dust extraction to control silica from concrete/screed.",
      "Prime and apply latex/levelling compound, mixing in a ventilated area to limit dust, and allow it to cure.",
      "Confirm the adhesive type and the room is ventilated before bonding; check no ignition sources where flammable adhesives are used.",
      "Set out and cut sheet/tiles with a sharp knife on a board, drawing the blade away from the body and replacing blades regularly.",
      "Spread adhesive and lay the finish from the kneeling position, using kneeling protection and rotating to relieve knee load.",
      "Roll and dress the finish, keep windows/doors open while solvents flash off, and store adhesive tins safely away from ignition.",
    ],
    hazards: [
      {
        hazard:
          "Knee injury and musculoskeletal damage from kneeling on hard floors throughout the working day",
        who: ["Floor layers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Provide and require proper kneeling protection — knee pads or kneeling mats — for all kneeling work",
          "Use long-handled tools for spreading and rolling where the finish allows, to stand for part of the task",
          "Rotate operatives and tasks to limit continuous kneeling and report knee problems early",
        ],
        residual: { likelihood: 3, severity: 2 },
      },
      {
        hazard:
          "Flammable solvent vapour from contact adhesives in unventilated rooms — fire/explosion and vapour inhalation",
        who: ["Floor layers", "Others on site"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Substitute low-VOC/water-based adhesives where the floor spec allows",
          "Ventilate the room thoroughly before, during and after bonding, and remove all ignition sources (DSEAR)",
          "Keep only working quantities at the face with lids on, wear RPE where vapour is significant, and store the rest in fire-rated storage",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Respirable crystalline silica from grinding/scarifying concrete and screed subfloors",
        who: ["Floor layers", "Adjacent trades"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Grind only with on-tool dust extraction (M/H-class) or water suppression; never dry-grind without extraction",
          "Section off the area and clean by HEPA vacuum, not sweeping",
          "FFP3 RPE for grinding and health surveillance for regular exposure",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Manual handling of heavy flooring rolls — full rolls of vinyl/carpet weigh 30-50 kg+ and are awkward to carry and position",
        who: ["Floor layers", "Labourers"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Order rolls cut to manageable lengths and deliver to the room of use to cut carrying",
          "Two-person handling or a roll trolley/lifter for full rolls; keep loads close and avoid twisting",
          "Clear access routes and lift in stages rather than carrying the full roll alone",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Knife cuts when trimming sheet and resilient flooring with a Stanley/hook knife",
        who: ["Floor layers"],
        initial: { likelihood: 4, severity: 2 },
        controls: [
          "Use a sharp blade and replace dull blades; cut on a board with a straight edge",
          "Always draw the blade away from the body and keep the free hand behind the cut line",
          "Retract/sheath blades when not cutting and dispose of used blades in a sharps container",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Latex/levelling-compound and primer dust and skin contact during subfloor preparation",
        who: ["Floor layers"],
        initial: { likelihood: 3, severity: 2 },
        controls: [
          "Mix low and slowly in a ventilated area and wear FFP3 while bag-emptying/mixing",
          "Wear nitrile gloves to avoid skin contact with primers and compounds",
          "Damp-clean/vacuum spillages and follow the product COSHH assessment",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "Knee pads/kneeling mats",
      "FFP3 mask for grinding/mixing",
      "Nitrile gloves",
      "Eye protection",
      "Cut-resistant glove on the non-knife hand",
      "Hi-vis and safety boots",
    ],
    competence: [
      "CSCS card and NVQ/SVQ in Floorcovering or equivalent experience",
      "Abrasive-wheels/floor-grinder training (PUWER)",
      "COSHH and DSEAR awareness for adhesives and dust",
      "Manual-handling briefing for roll handling",
    ],
    permits: [
      "Hot-work prohibition/area control where flammable adhesives are in use (DSEAR)",
      "Dust/grinding area agreement where on-tool extraction water is supplied",
    ],
    equipment: [
      "Floor grinder/scarifier with dust extraction",
      "Forced-action mixer for latex/levelling compound",
      "Notched trowels and adhesive spreaders",
      "Knives, straight edges and a cutting board",
      "Floor/seam roller and roll trolley/lifter",
      "HEPA/M-class vacuum",
    ],
    pcChecks: [
      "That flammable adhesives are controlled by ventilation and ignition-source removal under DSEAR, not just 'good ventilation'",
      "That subfloor grinding uses on-tool extraction/water suppression with FFP3 for RCS, not dry grinding",
      "That knee/MSD protection is specified for all-day kneeling, and roll handling is two-person/assisted",
    ],
    faqs: [
      {
        q: "Are contact adhesives a fire risk indoors?",
        a: "Yes. Many flooring contact adhesives are solvent-based and give off heavy, flammable vapour that can collect at floor level in an unventilated room and ignite from a spark or pilot light. They fall under DSEAR, so the room must be well ventilated before, during and after bonding, all ignition sources removed, and only working quantities kept at the workface with the rest in fire-rated storage. Where the floor specification allows, a low-VOC water-based adhesive removes most of the risk. The RAMS should state which adhesive is used and the ventilation arrangements.",
      },
      {
        q: "Do floor layers need to worry about silica dust?",
        a: "Yes, whenever they grind, scarify or scabble concrete or screed subfloors. That dust contains respirable crystalline silica, which causes silicosis, so grinders must run with on-tool dust extraction or water suppression — never dry-grinding into the open air. The area should be sectioned off, cleaned with a HEPA vacuum rather than swept, and FFP3 RPE worn. A flooring RAMS that covers adhesives but ignores subfloor-prep silica is incomplete.",
      },
    ],
  },

  "trade-tiling": {
    whoFor:
      "Wall and floor tilers fixing ceramic, porcelain and natural-stone tiles should give the principal contractor this tiling risk assessment before they begin. The trade's signature risks — silica from wet-cutting porcelain, dermatitis from adhesives and grout, and handling heavy large-format tiles — are not addressed by a generic finishing RAMS. It is written for the tiler cutting and fixing and the operative mixing adhesive and serving large boards.",
    scope:
      "This RAMS covers the preparation of backgrounds and the cutting, fixing and grouting of ceramic, porcelain, mosaic and natural-stone wall and floor tiles using cement-based and reaction-resin adhesives and grouts. It covers wet-cutting and handling of large-format tiles. It does not cover the laying or levelling of the screed/subfloor, removal of existing tiles containing asbestos backing, or work at height off scaffold, which are covered separately. Cutting is by wet method and large tiles are handled two-person or with suction lifters.",
    legislation: [
      "Construction (Design and Management) Regulations 2015",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — RCS, adhesives, grout",
      "EH40/2005 Workplace Exposure Limits — respirable crystalline silica",
      "Manual Handling Operations Regulations 1992 (as amended)",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER) — tile saws",
      "HSE CIS26 — cement and skin contact",
    ],
    sequence: [
      "Prepare and prime the background, checking it is sound, level and clean before setting out.",
      "Set out the tiles dry, planning cuts to minimise cutting of dense porcelain.",
      "Cut tiles on a water-fed wet saw or by score-and-snap, keeping water suppression topped up and never dry-cutting porcelain with a grinder.",
      "Mix adhesive in a ventilated area wearing gloves, decanting powder low and slowly to limit dust.",
      "Fix tiles to the background, handling large-format tiles two-person or with suction lifters and kneeling on protection for floor work.",
      "Grout, sponge and finish, wearing gloves throughout, then clean tools and settle out washings rather than discharging to drains.",
    ],
    hazards: [
      {
        hazard:
          "Respirable crystalline silica from cutting porcelain and ceramic tiles — dry-cutting dense porcelain generates very high silica dust",
        who: ["Tilers", "Adjacent trades"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Plan the layout to minimise cuts and use score-and-snap for straight cuts where possible",
          "Cut only on a water-fed wet saw with the suppression working; never dry-cut porcelain with an angle grinder",
          "Where any dry cutting is unavoidable, use on-tool extraction and FFP3, and clean by vacuum not sweeping",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Dermatitis and cement burns from prolonged contact with adhesives and grout — cement-based products are alkaline and abrasive",
        who: ["Tilers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Use mechanical mixing and tools to reduce direct hand contact with adhesive and grout",
          "Wear nitrile gloves throughout fixing and grouting and avoid sponging with bare hands",
          "Provide washing facilities and barrier/after-work cream; wash contact off promptly (CIS26)",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Manual handling of large-format tiles — porcelain slabs are heavy, awkward and easily cracked, risking back injury and trapped fingers",
        who: ["Tilers", "Labourers"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Use vacuum/suction lifters and a tile transport frame for large-format slabs",
          "Two-person handling for big tiles, keeping loads close and avoiding twisting; plan the route and resting points",
          "Wear gloves and protect fingers when positioning and bedding heavy tiles",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Cuts and lacerations from sharp tile edges and snapped/glass tiles",
        who: ["Tilers"],
        initial: { likelihood: 4, severity: 2 },
        controls: [
          "Handle cut and snapped tiles with cut-resistant gloves and dress sharp edges",
          "Use the correct cutter/nippers and keep the free hand clear when snapping",
          "Clear offcuts and broken tiles promptly into a dedicated waste container",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Knee injury from kneeling on hard floors during floor tiling and grouting",
        who: ["Tilers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Provide and require knee pads/kneeling mats for all floor work",
          "Rotate kneeling and standing tasks and use long-handled tools where practicable",
          "Take micro-breaks and report knee discomfort early",
        ],
        residual: { likelihood: 3, severity: 2 },
      },
    ],
    ppe: [
      "FFP3 mask for any tile cutting",
      "Nitrile gloves for adhesive/grout",
      "Cut-resistant gloves for handling",
      "Knee pads/kneeling mats",
      "Eye protection when cutting",
      "Hi-vis and safety boots",
    ],
    competence: [
      "CSCS card and NVQ/SVQ in Wall and Floor Tiling or equivalent experience",
      "Tile-saw use and abrasive-wheels awareness (PUWER)",
      "COSHH awareness for RCS, adhesives and grout",
      "Manual-handling briefing for large-format tiles",
    ],
    permits: [
      "Wet-cutting/dust area agreed where the saw needs a water supply",
      "Manual-handling assessment record for large-format tiling",
    ],
    equipment: [
      "Water-fed wet tile saw",
      "Manual score-and-snap cutter and tile nippers",
      "Forced-action adhesive mixer and notched trowels",
      "Vacuum/suction tile lifters and transport frame",
      "Grout floats, sponges and buckets",
      "Knee pads and HEPA vacuum",
    ],
    pcChecks: [
      "That porcelain/ceramic cutting is wet-cut or score-and-snap with FFP3, and dry-cutting porcelain with a grinder is prohibited",
      "That large-format tiles are handled two-person or with suction lifters, with a manual-handling assessment",
      "That dermatitis controls (gloves throughout, washing) and knee protection for floor work are explicit",
    ],
    faqs: [
      {
        q: "Do tilers really need face-fit tested masks?",
        a: "If there is any chance of dry-cutting or dust escaping during cutting, yes — cutting porcelain and ceramic releases respirable crystalline silica, which causes silicosis, and FFP3 respiratory protection must be face-fit tested to the individual to give the rated protection. The first control is to wet-cut so dust is suppressed at source, with score-and-snap for straight cuts, which greatly reduces the airborne dust. Where dry cutting is unavoidable, on-tool extraction plus a face-fit tested FFP3 is required. A loose, unfitted mask offers little real protection.",
      },
      {
        q: "How should we handle large-format porcelain tiles safely?",
        a: "Large-format porcelain slabs are heavy and awkward and crack easily, so they should be moved with vacuum/suction lifters and a transport frame rather than carried flat by hand, and positioned by two people keeping the load close and avoiding twisting. A manual-handling assessment should set out the loads, the route and resting points, and gloves protect fingers when bedding the tile. The tiling RAMS should name suction lifters and two-person handling specifically rather than rely on a generic 'lift correctly' line.",
      },
    ],
  },
};
